import { getToken } from "@/utils/auth";
import { HTTP_HEADERS } from "@/config/httpHead";
import { emitter } from "../mitt";

/**
 * 全局直播间同步 WebSocket 管理器
 * 用于跨平台同步直播间状态，包括口播队列和公屏信息
 */
class GlobalLiveRoomSyncWebSocket {
  private ws: WebSocket | null = null;
  private reconnectTimer: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private isDestroyed = false;
  private storageListener: ((event: StorageEvent) => void) | null = null;

  // 回调函数，用于更新直播间数据
  private onQueueUpdate: ((roomId: string, queueData: any) => void) | null =
    null;
  private onPublicMessageUpdate:
    | ((roomId: string, messageData: any) => void)
    | null = null;
  private onRoomStatusUpdate:
    | ((roomId: string, statusData: any) => void)
    | null = null;

  /**
   * 设置队列更新回调
   */
  setQueueUpdateCallback(callback: (roomId: string, queueData: any) => void) {
    this.onQueueUpdate = callback;
  }

  /**
   * 设置公屏消息更新回调
   */
  setPublicMessageUpdateCallback(
    callback: (roomId: string, messageData: any) => void
  ) {
    this.onPublicMessageUpdate = callback;
  }

  /**
   * 设置房间状态更新回调
   */
  setRoomStatusUpdateCallback(
    callback: (roomId: string, statusData: any) => void
  ) {
    this.onRoomStatusUpdate = callback;
  }

  /**
   * 移除所有回调
   */
  removeAllCallbacks() {
    this.onQueueUpdate = null;
    this.onPublicMessageUpdate = null;
    this.onRoomStatusUpdate = null;
  }

  /**
   * 连接 WebSocket
   */
  async connect(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isDestroyed) {
        resolve(false);
        return;
      }

      const token = getToken();
      if (!token) {
        resolve(false);
        return;
      }

      // 如果已有连接，先关闭
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsHost = window.location.host;
      let wsUrl = "";
      if (import.meta.env.VITE_API_BASE_WS) {
        wsUrl = `${import.meta.env.VITE_API_BASE_WS}/live/room-sync?token=${token}`;
      } else {
        wsUrl = `${wsProtocol}//${wsHost}${HTTP_HEADERS.Ws}/live/room-sync?token=${token}`;
      }

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          resolve(true);
        };

        this.ws.onmessage = event => {
          try {
            const data = JSON.parse(event.data);

            // 根据数据类型处理不同的同步信息
            switch (data.type) {
              case "queue_update":
                this.handleQueueUpdate(data);
                break;
              case "public_message_update":
                this.handlePublicMessageUpdate(data);
                break;
              case "room_status_update":
                this.handleRoomStatusUpdate(data);
                break;
              case "room_sync":
                this.handleRoomSync(data);
                break;
              default:
            }
          } catch (error) {}
        };

        this.ws.onerror = error => {
          reject(error);
        };

        this.ws.onclose = event => {
          this.ws = null;

          // 如果不是主动关闭且未达到最大重连次数，则重连
          if (
            !this.isDestroyed &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.isDestroyed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // 移除跨浏览器监听器
    this.removeCrossBrowserListener();
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 重置状态
   */
  reset() {
    this.isDestroyed = false;
    this.reconnectAttempts = 0;
  }

  /**
   * 安排重连
   */
  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      if (!this.isDestroyed) {
        this.connect().catch(error => {});
      }
    }, this.reconnectInterval);
  }

  /**
   * 处理队列更新
   */
  private handleQueueUpdate(data: any) {
    const { roomId, queue, currentIndex, isPlaying } = data;

    // 调用回调函数
    if (this.onQueueUpdate) {
      this.onQueueUpdate(roomId, { queue, currentIndex, isPlaying });
    }

    // 通过事件总线广播
    emitter.emit("queueSync", {
      roomId,
      queue: queue || [],
      currentIndex: currentIndex || 0,
      isPlaying: isPlaying || false,
      timestamp: Date.now()
    });

    // 跨浏览器同步
    this.syncToOtherTabs("queueSync", {
      roomId,
      queue: queue || [],
      currentIndex: currentIndex || 0,
      isPlaying: isPlaying || false,
      timestamp: Date.now()
    });
  }

  /**
   * 处理公屏消息更新
   */
  private handlePublicMessageUpdate(data: any) {
    const { roomId, messages, newMessage } = data;

    // 调用回调函数
    if (this.onPublicMessageUpdate) {
      this.onPublicMessageUpdate(roomId, { messages, newMessage });
    }

    // 通过事件总线广播
    emitter.emit("publicMessageSync", {
      roomId,
      messages: messages || [],
      newMessage: newMessage || false,
      timestamp: Date.now()
    });

    // 跨浏览器同步
    this.syncToOtherTabs("publicMessageSync", {
      roomId,
      messages: messages || [],
      newMessage: newMessage || false,
      timestamp: Date.now()
    });
  }

  /**
   * 处理房间状态更新
   */
  private handleRoomStatusUpdate(data: any) {
    const { roomId, status, roomData } = data;

    // 调用回调函数
    if (this.onRoomStatusUpdate) {
      this.onRoomStatusUpdate(roomId, { status, roomData });
    }

    // 通过事件总线广播
    emitter.emit("roomStatusSync", {
      roomId,
      status: status || "preparing",
      timestamp: Date.now(),
      roomData: roomData || {}
    });

    // 跨浏览器同步
    this.syncToOtherTabs("roomStatusSync", {
      roomId,
      status: status || "preparing",
      timestamp: Date.now(),
      roomData: roomData || {}
    });
  }

  /**
   * 处理房间完整同步
   */
  private handleRoomSync(data: any) {
    const { roomId, roomData } = data;

    // 通过事件总线广播
    emitter.emit("roomFullSync", {
      roomId,
      roomData: roomData || {},
      timestamp: Date.now()
    });

    // 跨浏览器同步
    this.syncToOtherTabs("roomFullSync", {
      roomId,
      roomData: roomData || {},
      timestamp: Date.now()
    });
  }

  /**
   * 发送队列状态
   */
  sendQueueStatus(roomId: string, queueData: any) {
    if (this.isConnected() && this.ws) {
      this.ws.send(
        JSON.stringify({
          type: "queue_update",
          roomId,
          ...queueData
        })
      );
    }
  }

  /**
   * 发送公屏消息
   */
  sendPublicMessage(roomId: string, messageData: any) {
    if (this.isConnected() && this.ws) {
      this.ws.send(
        JSON.stringify({
          type: "public_message_update",
          roomId,
          ...messageData
        })
      );
    }
  }

  /**
   * 发送房间状态
   */
  sendRoomStatus(roomId: string, statusData: any) {
    if (this.isConnected() && this.ws) {
      this.ws.send(
        JSON.stringify({
          type: "room_status_update",
          roomId,
          ...statusData
        })
      );
    }
  }

  /**
   * 请求房间完整同步
   */
  requestRoomSync(roomId: string) {
    if (this.isConnected() && this.ws) {
      this.ws.send(
        JSON.stringify({
          type: "room_sync_request",
          roomId
        })
      );
    }
  }

  /**
   * 初始化跨浏览器监听器
   */
  initCrossBrowserListener() {
    // 移除之前的监听器
    this.removeCrossBrowserListener();

    // 创建新的监听器
    this.storageListener = (event: StorageEvent) => {
      if (event.key === "queueSync") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          emitter.emit("queueSync", data);
        } catch (error) {}
      } else if (event.key === "publicMessageSync") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          emitter.emit("publicMessageSync", data);
        } catch (error) {}
      } else if (event.key === "roomStatusSync") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          emitter.emit("roomStatusSync", data);
        } catch (error) {}
      } else if (event.key === "roomFullSync") {
        try {
          const data = JSON.parse(event.newValue || "{}");
          emitter.emit("roomFullSync", data);
        } catch (error) {}
      }
    };

    // 添加监听器
    window.addEventListener("storage", this.storageListener);
  }

  /**
   * 移除跨浏览器监听器
   */
  removeCrossBrowserListener() {
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
      this.storageListener = null;
    }
  }

  /**
   * 同步到其他标签页
   */
  private syncToOtherTabs(eventType: string, data: any) {
    try {
      localStorage.setItem(eventType, JSON.stringify(data));
      // 立即删除，避免存储过多数据
      setTimeout(() => {
        localStorage.removeItem(eventType);
      }, 100);
    } catch (error) {}
  }
}

export const globalLiveRoomSyncWebSocket = new GlobalLiveRoomSyncWebSocket();
